import { QueryFunction, useQuery } from '@tanstack/react-query';
import { BASE_PRICE_POOL } from 'config/networks';
import {
  BalancerV2PriceProviderAddress,
  ChainlinkV3PriceProviderAddress,
  GOHMPriceProviderAddress,
  UniswapV3PriceProviderAddress,
  UniswapV3PriceProviderV2Address,
  UniswapV3PriceProviderV3Address,
  WSTETHPriceProviderAddress,
} from '@silo/addresses';
import { useWeb3Context, Web3Context } from 'app/queries/web3Context';
import { Contract } from 'ethers';
import { FC } from 'react';
import {
  BalancerV2PriceProvider,
  BalancerV2PriceProvider__factory,
  ChainlinkV3PriceProvider,
  ChainlinkV3PriceProvider__factory,
  UniswapV3PriceProvider,
  UniswapV3PriceProviderV2,
  UniswapV3PriceProviderV2__factory,
  UniswapV3PriceProviderV3,
  UniswapV3PriceProviderV3__factory,
  UniswapV3PriceProvider__factory,
} from '@silo/typechain';
import { AddressZero } from '@ethersproject/constants';
import { getExplorerLink } from '@silo/web3';
import {
  ChainIdLegacy,
  ChecksumAddress,
  LINKS,
  OracleKey,
  PROTOCOL_INSTANCE_CHAIN,
  ProtocolInstance,
  getOracle,
  getProtocolInstanceChainId,
} from '@silo/types';
import { shortenHash } from '@silo/utils';
import { Button, Dots, ExternalLink, TooltipDescription } from '@silo/ui';

export const OracleLabel: FC<OracleDescriptionProps> = ({ assetAddress, assetSymbol, oracleKey }) => {
  return (
    <div className="flex gap-1.5 items-center">
      <span>Oracle</span>
      <TooltipDescription>
        <OracleDescription assetAddress={assetAddress} assetSymbol={assetSymbol} oracleKey={oracleKey} />
      </TooltipDescription>
    </div>
  );
};

interface OracleDescriptionProps {
  assetAddress: ChecksumAddress;
  assetSymbol: string;
  oracleKey: OracleKey;
  hideLearnMoreLink?: boolean;
}

export const OracleDescription: FC<OracleDescriptionProps> = ({
  assetAddress,
  assetSymbol,
  oracleKey,
  hideLearnMoreLink = false,
}) => {
  const web3Context = useWeb3Context();

  const { data: pools, isLoading } = useQuery(
    ['oraclePool', web3Context!, { assetAddress, assetSymbol, oracleKey }],
    queryOraclePools,
    {
      enabled: !!web3Context,
    }
  );

  return (
    <div className="flex flex-col gap-2.5">
      <p>Asset price is determined by an external oracle that is not controlled by Silo Protocol.</p>
      <p>Oracles pose risk to users. Only governance can change price oracles. </p>
      {!hideLearnMoreLink && (
        <ExternalLink variant="action" size="small" className="w-fit" href={LINKS.SILOPEDIA.ORACLE_RISK}>
          Learn more at Silopedia
        </ExternalLink>
      )}

      {pools ? (
        pools.type === 'SinglePool' ? (
          <ExternalLink variant="action" size="small" className="w-fit" href={pools.url}>
            {pools.label}
          </ExternalLink>
        ) : (
          pools.pools.map((pool) => {
            return (
              <ExternalLink
                href={pool.url}
                size="small"
                variant="action"
                className="flex items-center justify-center gap-2 w-fit"
              >
                {pool.label}
              </ExternalLink>
            );
          })
        )
      ) : isLoading ? (
        <Button variant="primary" disabled className="bg-shale-600">
          <Dots>Loading</Dots>
        </Button>
      ) : null}
    </div>
  );
};

const oraclePoolKey = 'oraclePool';

type OraclePoolKey = [key: typeof oraclePoolKey, web3Context: Web3Context, asset: OracleDescriptionProps];

type SinglePool = { type: 'SinglePool'; url: string; label: string };
type MultiplePools = {
  type: 'MultiplePools';
  pools: { poolAddress: string; url: string; label: string }[];
};

type OraclePools = SinglePool | MultiplePools | null;

const queryOraclePools: QueryFunction<OraclePools, OraclePoolKey> = async ({
  queryKey: [, { chainId, clientProvider, protocolInstance }, { assetAddress, assetSymbol, oracleKey }],
}) => {
  const { label } = getOracle(oracleKey);

  switch (oracleKey) {
    case 'balancer': {
      const balancerV2PriceProvider = new Contract(
        BalancerV2PriceProviderAddress[protocolInstance],
        BalancerV2PriceProvider__factory.abi,
        clientProvider
      ) as BalancerV2PriceProvider;

      const { poolId } = await balancerV2PriceProvider.assetsPools(assetAddress);

      return { type: 'SinglePool', url: createBalancerPoolLink(poolId), label: `View ${label} pool` };
    }
    case 'uniswap-v3': {
      const uniswapV3PriceProvider = new Contract(
        UniswapV3PriceProviderAddress[protocolInstance],
        UniswapV3PriceProvider__factory.abi,
        clientProvider
      ) as UniswapV3PriceProvider;

      const quoteToken = await uniswapV3PriceProvider.quoteToken();

      if (quoteToken === assetAddress) {
        const chainId = getProtocolInstanceChainId(protocolInstance);

        return {
          type: 'SinglePool',
          url: createUniswapPoolLink(BASE_PRICE_POOL[chainId]),
          label: `View ${label} pool`,
        };
      }

      const poolId = await uniswapV3PriceProvider.pools(assetAddress);

      return { type: 'SinglePool', url: createUniswapPoolLink(poolId), label: `View ${label} pool` };
    }
    case 'uniswap-v3-v2': {
      const uniswapV3PriceProviderV2 = new Contract(
        UniswapV3PriceProviderV2Address[protocolInstance],
        UniswapV3PriceProviderV2__factory.abi,
        clientProvider
      ) as UniswapV3PriceProviderV2;

      const quoteToken = await uniswapV3PriceProviderV2.quoteToken();

      if (quoteToken === assetAddress) {
        const chainId = getProtocolInstanceChainId(protocolInstance);

        return {
          type: 'SinglePool',
          url: createUniswapPoolLink(BASE_PRICE_POOL[chainId]),
          label: `View ${label} pool`,
        };
      }

      const pools = await uniswapV3PriceProviderV2.pools(assetAddress);

      return {
        type: 'MultiplePools',
        pools: pools
          .map(({ pool }) => pool)
          .map((pool) => ({
            poolAddress: pool,
            url: createUniswapPoolLink(pool),
            label: `View ${shortenHash(pool)} pool`,
          })),
      };
    }
    case 'uniswap-v3-v3': {
      const uniswapV3PriceProviderV3 = new Contract(
        UniswapV3PriceProviderV3Address[protocolInstance],
        UniswapV3PriceProviderV3__factory.abi,
        clientProvider
      ) as UniswapV3PriceProviderV3;

      const quoteToken = await uniswapV3PriceProviderV3.quoteToken();

      if (quoteToken === assetAddress) {
        const chainId = getProtocolInstanceChainId(protocolInstance);

        return {
          type: 'SinglePool',
          url: createUniswapPoolLink(BASE_PRICE_POOL[chainId]),
          label: `View ${label} pool`,
        };
      }

      const pools = await uniswapV3PriceProviderV3.pools(assetAddress);

      return {
        type: 'MultiplePools',
        pools: pools
          .map(({ pool }) => pool)
          .map((pool) => ({
            poolAddress: pool,
            url: createUniswapPoolLink(pool),
            label: `View ${shortenHash(pool)} pool`,
          })),
      };
    }
    case 'chainlink':
    case 'stEUR-oracle': {
      const chainlinkPriceProvider = new Contract(
        ChainlinkV3PriceProviderAddress[protocolInstance],
        ChainlinkV3PriceProvider__factory.abi,
        clientProvider
      ) as ChainlinkV3PriceProvider;

      const assetData = await chainlinkPriceProvider.assetData(assetAddress);

      if (assetData.aggregator === AddressZero)
        return { type: 'SinglePool', url: LINKS.CHAINLINK.ORACLE_ADDRESSES, label: `View ${label} price feed` };

      return {
        type: 'SinglePool',
        url: createExplorerLink(assetData.aggregator, chainId),
        label: `View ${label} price feed`,
      };
    }
    case 'wstETH-oracle': {
      return {
        type: 'SinglePool',
        url: createExplorerLink(WSTETHPriceProviderAddress[protocolInstance], chainId),
        label: `View price provider`,
      };
    }
    case 'gOHM-oracle': {
      return {
        type: 'SinglePool',
        url: createExplorerLink(GOHMPriceProviderAddress[protocolInstance], chainId),
        label: `View price provider`,
      };
    }
    case 'dia': {
      return {
        type: 'SinglePool',
        url: getDiaPriceProviderLink(protocolInstance, assetAddress),
        label: `View price provider`,
      };
    }
    case 'redstone': {
      return {
        type: 'SinglePool',
        url: createExplorerLink('0x8751F736E94F6CD167e8C5B97E245680FbD9CC36', chainId),
        label: `View price provider`,
      };
    }
    case 'chronicle': {
      return {
        type: 'SinglePool',
        url: getChronicalPriceProviderLink(protocolInstance, assetSymbol),
        label: `View price provider`,
      };
    }

    default: {
      return null;
    }
  }
};

const getDiaPriceProviderLink = (protocolInstance: ProtocolInstance, assetAddress: ChecksumAddress) => {
  if (protocolInstance !== 'arbitrum') {
    return createExplorerLink('0xe871e9bd0ccc595a626f5e1657c216ce457cea43', PROTOCOL_INSTANCE_CHAIN[protocolInstance]);
  }

  return `https://www.diadata.org/app/price/asset/Arbitrum/${assetAddress}/`;
};

const getChronicalPriceProviderLink = (protocolInstance: ProtocolInstance, assetSymbol: string) => {
  type ChronicalChains = 'arbitrum' | 'optimism' | 'base';

  const protocolMap: Record<ChronicalChains, string> = {
    arbitrum: 'ARB',
    optimism: 'OPT',
    base: 'BASE',
  };

  const blockChainStr = protocolMap[protocolInstance as ChronicalChains] || '';

  return `https://chroniclelabs.org/dashboard/oracle/${assetSymbol.toUpperCase()}/USD?blockchain=${blockChainStr}`;
};

/**
 * TODO: look into typing these as `ChecksumAddress`
 */
const createBalancerPoolLink = (poolId: string): string => `https://app.balancer.fi/#/pool/${poolId.toLowerCase()}`;

const createUniswapPoolLink = (poolId: string): string => `https://info.uniswap.org/#/pools/${poolId.toLowerCase()}`;

const createExplorerLink = (address: string, chainId: ChainIdLegacy): string =>
  getExplorerLink(chainId, address, 'address');
